'use client';

import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import type { AiNote } from '@/lib/ai-search';

export interface AiSearchResult {
  id: number | null; url: string; notes: AiNote[]; unmapped: string[];
  charged: number; filtersApplied: number;
}

const rangeSchema = z.object({ min: z.number().nullable().optional(), max: z.number().nullable().optional() });
export const aiNoteSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('rounded'), field: z.string(), requested: rangeSchema, applied: rangeSchema }),
  z.object({ kind: z.literal('dropped'), field: z.string() }),
  z.object({ kind: z.literal('sort_unsupported') }),
  z.object({ kind: z.literal('unmapped'), text: z.string() }),
  z.object({ kind: z.literal('backend'), code: z.string(), params: z.record(z.string(), z.unknown()).optional() }),
]);
export const aiSearchResultSchema = z.object({
  id: z.number().nullable(), url: z.string(), notes: z.array(aiNoteSchema), unmapped: z.array(z.string()),
  charged: z.number(), filtersApplied: z.number(),
});
export const aiSearchEntrySchema = aiSearchResultSchema.omit({ filtersApplied: true }).extend({ query: z.string() });

export class AiSearchRequestError extends Error {
  constructor(public status: number, public code: string) { super(code); }
}

export function useAiSearch() {
  return useMutation({ mutationFn: async (input: { query: string; locale: 'de' | 'en'; uiLocale: string }): Promise<AiSearchResult> => {
    const response = await fetch('/api/search/ai', { method: 'POST',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) });
    const json: unknown = await response.json();
    if (!response.ok) {
      const code = json && typeof json === 'object' && 'code' in json && typeof json.code === 'string' ? json.code : 'ai_search_unavailable';
      throw new AiSearchRequestError(response.status, code);
    }
    return aiSearchResultSchema.parse(json);
  } });
}

export function useAiSearchFeedback() {
  return useMutation({ mutationFn: async (input: { id: number; value: -1 | 1 }) => {
    const response = await fetch('/api/search/ai/feedback', { method: 'POST',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) });
    if (!response.ok) throw new Error('feedback_failed');
  } });
}
